import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { compose } from 'recompose';

import requestCountry from 'js/lib/requestCountry';
import { stringKeyToTuple } from 'js/lib/stringKeyTuple';
import { useLocation } from 'js/lib/useRouter';
import user from 'js/lib/user';

import EnrollmentChoiceTypes from 'bundles/enroll-common/common/EnrollmentChoiceTypes';
import {
  choiceTypeToHandleSubmitPromise,
  submitEnrollmentPromise,
} from 'bundles/enroll-common/lib/enrollmentChoiceUtils';
import type { ApiError } from 'bundles/enroll-common/utils/errorUtils';
import EnrollErrorModal from 'bundles/enroll-modal/components/common/EnrollErrorModal';
import CredentialTrackEnrollModal from 'bundles/enroll-modal/components/credential-track/CredentialTrackEnrollModal';
import EoiWidgetContainer from 'bundles/expression-of-interest/components/xddp/EoiWidgetContainer';
import { ACTION_TYPES } from 'bundles/expression-of-interest/constants';
import type { EoiButtonType } from 'bundles/expression-of-interest/types';
import type ProductSkusV1 from 'bundles/naptimejs/resources/productSkus.v1';
import type PromotionEligibilitiesV1 from 'bundles/naptimejs/resources/promotionEligibilities.v1';
import { withCredentialTrackProductSKUs } from 'bundles/ownable-products-common/components/xdp/withNaptimeData';
import ProductTypeObject from 'bundles/payments-common/common/ProductType';
import TopLevelModal from 'bundles/phoenix/components/TopLevelModal';
import type { EnrollmentFlowProps } from 'bundles/premium-certificate/types';
import { trackApplyClick } from 'bundles/premium-certificate/utils';
import withPromotionInfo, { withPromoCodeFromUrl } from 'bundles/promotions/components/withPromotionInfo';

import _t from 'i18n!nls/premium-certificate';

export type Props = EnrollmentFlowProps & {
  id?: string;
  productSkus?: Array<ProductSkusV1>;
  promotionEligibilities?: PromotionEligibilitiesV1;
};

const BoomerangFlow = ({
  id,
  actionBarData,
  slug,
  expressionOfInterestContainer,
  productSkus,
  promotionEligibilities,
  certificateName,
  certificateType,
}: Props) => {
  const [showEnrollModal, setEnrollModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [error, setError] = useState<ApiError | undefined>(undefined);
  const handleError = useCallback<(...args: $TSFixMe[]) => $TSFixMe>((e: ApiError) => {
    setError(e);
    setEnrollModal(false);
    setShowErrorModal(true);
  }, []);
  const credentialTrackProductSku = productSkus?.find((sku) => sku.productType === ProductTypeObject.CREDENTIAL_TRACK);

  // we want to show the one-payment option first over the installments
  const credentialTrackSubscriptionProductSkus = useMemo(
    () => productSkus?.filter((sku) => sku.productType !== ProductTypeObject.CREDENTIAL_TRACK),
    [productSkus]
  );

  const location = useLocation();

  useEffect(() => {
    if (location?.query?.showPaymentModal && productSkus?.length) {
      trackApplyClick(slug, id, certificateName, certificateType);

      // We are enabling onetime payments for MTC in India.
      const countryCode = requestCountry.get();
      if (user.isAuthenticatedUser()) {
        if (credentialTrackProductSku && countryCode === 'IN') {
          const [, productItemId] = stringKeyToTuple(credentialTrackProductSku.id);
          const options = {
            credentialTrackProductId: productItemId,
            productType: credentialTrackProductSku.productType,
          };
          const handleSubmitPromise =
            choiceTypeToHandleSubmitPromise[EnrollmentChoiceTypes.ENROLL_THROUGH_CREDENTIALTRACK];
          const promoCode = promotionEligibilities?.isEligible ? promotionEligibilities?.promoCodeId : undefined;

          submitEnrollmentPromise({
            handleSubmitPromise,
            options,
            promoCode,
          }).catch(handleError);
        } else {
          setEnrollModal(true);
        }
      }
    }
    // Intentionally including empty array to ensure this only runs once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let enrollButton: EoiButtonType | null = null;

  if (expressionOfInterestContainer?.applyButtonUrl) {
    const enrollButtonUrl = expressionOfInterestContainer.applyButtonUrl;
    const enrollButtonText = expressionOfInterestContainer?.applyButtonText;

    enrollButton = {
      actionType: ACTION_TYPES.apply,
      doAction: () => trackApplyClick(slug, id, certificateName, certificateType),
      isExternalLink: true,
      buttonOptions: {
        label: enrollButtonText || _t('Apply now'),
        trackingName: 'enroll',
        trackingData: { section: 'headerBanner' },
        tag: 'a',
        htmlAttributes: {
          href: enrollButtonUrl,
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      },
    };
  }
  const emailMeInfoButton: EoiButtonType = { actionType: ACTION_TYPES.emailMeInfo };

  return (
    <div data-test="BoomerangFlow">
      {showEnrollModal && (
        <TopLevelModal>
          <CredentialTrackEnrollModal
            productSkus={credentialTrackSubscriptionProductSkus}
            onClose={() => setEnrollModal(false)}
            certificateType={certificateType}
          />
        </TopLevelModal>
      )}
      {showErrorModal && (
        <TopLevelModal>
          <EnrollErrorModal error={error} onClose={() => setShowErrorModal(false)} />
        </TopLevelModal>
      )}
      <EoiWidgetContainer
        slug={slug}
        degreeId={id || ''}
        actionBarData={actionBarData}
        applicationUrl={expressionOfInterestContainer?.applyButtonUrl || ''}
        programInterestText={expressionOfInterestContainer?.programInterestText || ''}
        enrollButtonData={enrollButton}
        emailMeInfoButtonData={emailMeInfoButton}
      />
    </div>
  );
};

export default compose<Props, EnrollmentFlowProps & { id?: string }>(
  withCredentialTrackProductSKUs(),
  withPromoCodeFromUrl(),
  withPromotionInfo()
)(BoomerangFlow);
